<template>
  <div v-if="widget" class="widget-form-container">
    <div v-for="(variant, idx) in widget.variants" :key="idx" class="section-container">
      <substrate-mount>
        <div class="attachment-input-container">
          <input-price
            ref="amountFrom"
            v-model.lazy="variant.amountFrom"
            :input-id="`variant.amountFrom.${idx}`"
            :input-name="`${$t('showing.for.donation.from')}, ${currency.sign}`"
            :min-value="0"
            :currency="currency"
            :error="isRepeatedValues(idx)"
            attachment
            @change="variant.amountFrom = $event"
          />
          <attachment-image
            :link="variant.attachment.link"
            :input-id="`variant.attachment.${idx}`"
            type="secondary"
            composite
            @loading="onImageLoading"
            @uploaded="data => variant.attachment.setData(data)"
            @reset="variant.attachment.reset()"
          />
        </div>

        <form-delete-icon
          v-if="!isDesktop && isSeveralVariantsAvailable"
          @click="onRemoveVariant(idx)"
        />
      </substrate-mount>

      <form-delete-icon
        v-if="isDesktop && isSeveralVariantsAvailable"
        @click="onRemoveVariant(idx)"
      />
    </div>

    <div v-if="!isExceededVariantsCount" class="panel-container">
      <capusta-panel
        icon="circle-plus"
        :description="$t('add.variant')"
        @click="onAddVariant"
      />
    </div>

    <div class="button-primary-container">
      <cs-button :width="buttonWidth" :disabled="!isValid" @click="onSubmit">
        {{ $t('common.proceed') }}
      </cs-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import attachmentMixin from 'lib/mixins/attachmentMixin';
import NotificationWidget from 'services/widget/NotificationWidget';

import AttachmentImage from 'modules/AttachmentImage';
import CapustaPanel from 'modules/CapustaLayout/CapustaPanel';

import InputPrice from '@/components/inputElements/InputPrice';
import SubstrateMount from '@/components/capusta/SubstrateMount';
import FormDeleteIcon from '@/components/capusta/FormDeleteIcon';

const MAX_VARIANTS = 10;

export default {
  name: 'NotificationWidget',
  mixins: [
    attachmentMixin,
  ],
  components: {
    InputPrice,
    AttachmentImage,
    SubstrateMount,
    FormDeleteIcon,
    CapustaPanel,
  },
  props: {
    widgets: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.initialize();
  },
  data() {
    return {
      widget: null,
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    isUniqueAmountValues() {
      const { variants } = this.widget;
      const amountList = variants.map(variant => variant.normalizeAmount());
      const amountUniqueList = new Set([...amountList]);

      return amountList.length === amountUniqueList.size;
    },
    isValid() {
      if (this.submittingDisabled) {
        return false;
      }

      if (!this.isUniqueAmountValues) {
        return false;
      }

      return true;
    },
    isSeveralVariantsAvailable() {
      return this.widget.variants.length > 1;
    },
    isExceededVariantsCount() {
      return this.widget.variants.length >= MAX_VARIANTS;
    },
    buttonWidth() {
      return this.isDesktop ? 223 : '100%';
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters(['isDesktop']),
  },
  methods: {
    isRepeatedValues(idx) {
      const { variants } = this.widget;
      const { amountFrom } = variants[idx];

      const repeatedValues = variants
        .filter(variant => Number(variant.amountFrom) === Number(amountFrom));

      return repeatedValues.length > 1;
    },
    onAddVariant() {
      if (this.isExceededTariffsCount) {
        return;
      }

      this.widget.addVariant();
    },
    onRemoveVariant(idx) {
      this.widget.deleteVariant(idx);
      // TODO: багфикс для обновления $refs и корректной валидации
      this.$nextTick(() => {
        this.widget.variants = this.widget.variants.map(variant => variant);
      });
    },
    onSubmit() {
      this.$emit('submit', this.widget.getData());
    },
    initialize() {
      this.widget = new NotificationWidget({
        currency: this.currency,
      });

      const data = this.widgets
        .find(widget => widget.type === this.widget.type);

      if (this.$validate.isUndefined(data)) {
        return;
      }

      this.$nextTick(async () => {
        this.widget.setData({
          ...data,
          currency: this.currency,
        });
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import '~styles/components/widgets/widget-form'

  .section-container
    position: relative
    margin-bottom: 20px

  .controls-container
    display: flex
    justify-content: space-between

  .attachment-input-container
    display: flex
    align-items: center

    .input
      flex: 1
      margin: 0
      height: 78px

      label::before
        border-radius: 8px 0 0 8px

    label.attach-image-label
      background-color: #131212
      border-radius: 0 8px 8px 0
</style>
